import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { site } from "@/mocks/site";
import { setPageMeta } from "@/utils/seo";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db8uehbk13ra8mig8v10";

const SERVICES = [
  {
    icon: "ri-film-line",
    title: "内容聚合",
    text: "持续整理日韩电视剧与电影资源，按类型、地区、年份与评分分类呈现，方便快速检索。",
  },
  {
    icon: "ri-translate-2",
    title: "中文字幕",
    text: "所有收录内容均标注中文字幕支持信息，帮助中文观众更轻松地理解剧情与对白。",
  },
  {
    icon: "ri-search-line",
    title: "便捷检索",
    text: "支持关键词搜索与多维度筛选，配合排序与分页，几步之内就能找到想看的影视。",
  },
  {
    icon: "ri-bookmark-line",
    title: "本地收藏",
    text: "收藏功能保存在你的浏览器本地，无需登录即可标记喜欢的影视，随时回来继续浏览。",
  },
];

type Status = "idle" | "loading" | "success" | "error";

export default function AboutPage() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    setPageMeta(
      "关于我们－日韩中文字幕在线观看",
      "了解日韩中文字幕在线观看的平台定位、服务范围、免责声明与联系方式，我们致力于为中文观众提供清晰的日韩影视浏览体验。",
    );
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，感谢您的反馈！");
      form.reset();
      return;
    }

    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFeedback("请完整填写称呼、邮箱与反馈内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFeedback("邮箱格式不正确，请检查后重试。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFeedback("反馈内容不能超过 500 个字符。");
      return;
    }

    data.delete("website_alt");
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, String(value)));

    setStatus("loading");
    setFeedback("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const payload = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;
      const serverMsg =
        payload?.meta?.message ||
        payload?.message ||
        payload?.meta?.detail ||
        responseText;

      if (response.ok && payload?.code === "OK") {
        setStatus("success");
        setFeedback(serverMsg ? String(serverMsg) : "提交成功，感谢您的反馈！");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(String(serverMsg || "提交失败，请稍后重试。"));
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

          <section className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-primary-950 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs text-background-50">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-information-line text-sm" />
              </span>
              关于我们
            </span>
            <h1 className="mt-4 font-brand text-2xl text-background-50 md:text-3xl">
              {site.name}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-primary-100/85 md:text-base">
              {site.description}
            </p>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-primary-100/85">
              我们是一群热爱日韩影视的中文观众，希望把好看的日韩电视剧与电影集中在一起，配上清晰的中文字幕信息与便捷的分类检索，让追剧这件事变得更简单、更舒服。
            </p>
          </section>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
          <section>
            <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-950 md:text-xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              服务说明
            </h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {SERVICES.map((service) => (
                <div
                  key={service.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${service.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 text-base font-medium text-foreground-950">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {service.text}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 text-base font-medium text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-shield-check-line text-lg" />
                </span>
                免责声明
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站为影视资源展示演示站点，站内影片资料仅用于分类浏览与信息查询演示，播放区域不具备真实播放能力，也不会显示播放成功状态。所有内容仅供学习、交流与界面演示使用，请勿用于任何商业用途。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 text-base font-medium text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-copyright-line text-lg" />
                </span>
                版权提示
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                站内出现的影视名称、海报与简介均为演示用途的示意内容。如您为相关权利人并认为内容存在不妥，欢迎通过下方联系表单与我们沟通，我们会尽快处理。
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-950 md:text-xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              联系我们
            </h2>
            <div className="mt-6 grid gap-8 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <p className="text-sm leading-relaxed text-foreground-600">
                  如果你对站内内容有建议、发现有误的信息，或希望推荐新的日韩影视，欢迎通过左侧表单或邮箱联系我们。
                </p>
                <ul className="mt-5 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    <span>
                      <span className="block text-foreground-500">联系邮箱</span>
                      <span className="text-foreground-900">{site.email}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-time-line text-lg" />
                    </span>
                    <span>
                      <span className="block text-foreground-500">回复时间</span>
                      <span className="text-foreground-900">工作日 1-3 天内回复</span>
                    </span>
                  </li>
                </ul>
              </div>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="lg:col-span-3 rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="text-sm text-foreground-700">
                      称呼
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      placeholder="请输入你的称呼"
                      className="mt-2 w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-sm text-foreground-700">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      placeholder="用于接收回复"
                      className="mt-2 w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-subject" className="text-sm text-foreground-700">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    placeholder="例如：内容建议 / 影片推荐"
                    className="mt-2 w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-message" className="text-sm text-foreground-700">
                    反馈内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="最多 500 个字符"
                    className="mt-2 w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-500"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-note-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {feedback && (
                  <p
                    className={`mt-4 text-sm ${
                      status === "success" ? "text-primary-700" : "text-accent-700"
                    }`}
                  >
                    {feedback}
                  </p>
                )}

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "loading" ? "提交中…" : "提交反馈"}
                  </button>
                  <span className="text-xs text-foreground-500">
                    提交即表示你同意我们仅将信息用于回复本次反馈
                  </span>
                </div>
              </form>
            </div>
          </section>

          <div className="mt-12 flex justify-center">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-home-line text-base" />
              </span>
              返回首页
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}